import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { getPresetFromCustomInput, getPresetFromString } from '../../presets';
import { useCanvasMediaScroll } from '../../utils/use-canvas-media-scroll';
import { useViewbox } from '../../utils/use-viewbox';
import { ScrollImagesContainer } from './scroll-images-container';
import { CustomMask, Mask, MaskType, PresetMask } from '../../types';
import { CustomTheme } from '../../theme';

const ScrollCanvas = styled.canvas<{ width: number; height: number }>`
  position: absolute;
  width: inherit;
  height: 100vh;
  margin: 0;
  padding: 0;
  pointer-events: none;
  .adobe-ue-edit &,
  .adobe-ue-preview & {
    // this variable is provided by the UE integration
    // it defines the initial viewport height in px
    // before the UE started resizing the iframe
    max-height: var(--ue-viewport-height);
  }
`;

export interface CanvasProps {
  children: ReactNode;
  position: number;
  mask: Mask;
  showGuide: boolean;
  theme: CustomTheme;
}

// eslint-disable-next-line react/function-component-definition
export const Canvas: React.FC<CanvasProps> = ({ children, position, mask, showGuide, theme }) => {
  const ref = React.useRef<HTMLCanvasElement>(null);

  const { width, height } = useViewbox();

  const maskConfig: PresetMask =
    mask.maskType === MaskType.maskCustom
      ? getPresetFromCustomInput(mask as CustomMask)
      : getPresetFromString(mask.text.replace(/\s/g, ''));

  useCanvasMediaScroll({
    color: theme?.colors.background,
    font: 'AudiType, sans-serif',
    mask: maskConfig,
    position,
    ref,
    showGuide,
  });

  return (
    <ScrollImagesContainer>
      {children}
      <ScrollCanvas
        ref={ref}
        width={width}
        height={height}
        data-testid="masktext"
        role="img"
        aria-label={mask.text}
      />
    </ScrollImagesContainer>
  );
};
